import * as Sentry from '@sentry/react';

/**
 * Thin fetch wrapper for the cs-analysis service.
 *
 * Differences vs general_fetch:
 *   - Hits global.config.cs_analysis_path (not server_path).
 *   - Adds X-Requested-With: cs-analysis-csp on every request — cs-analysis's
 *     auth guard requires this for CSRF protection.
 *   - Does NOT unwrap a `data` envelope. cs-analysis returns raw JSON.
 *   - Default method is GET (most endpoints are reads).
 *
 * Auth bootstrap. The legacy infurnia_main session cookie (`connect.sid`) is
 * host-only on preprodbackend.infurnia.com, so the browser refuses to send it
 * to cs-analysis subdomains. To unblock, on the first 401 we:
 *   1. Call `${server_path}/user/get_details` — the cookie *is* sent to that
 *      host even from another *.infurnia.com page (credentials:'include').
 *   2. POST the returned user_details to cs-analysis `/api/v1/auth/init` —
 *      which HMAC-signs it and sets a `cs-analysis-session` cookie with
 *      Domain=.infurnia.com. From then on, that cookie travels everywhere.
 *   3. Retry the original request once.
 */

let bootstrap_in_flight = null;
let bootstrap_done = false;
// Cached after the first introspection. Used to impersonate the real
// logged-in user when targeting a local cs-analysis backend (see below).
let cached_user_details = null;

/**
 * True when cs-analysis is running at localhost. In that mode the SameSite
 * cookie can't travel from devdesign2.infurnia.com to http://localhost, so
 * cs-analysis runs a permissive local bypass. To still surface the *real*
 * logged-in user (so role-based filtering can do its job) the frontend
 * sends x-local-user-{id,email,roles} headers on every request when targeting
 * localhost; cs-analysis reads those instead of falling back to a synthetic
 * admin. Curl from terminal still gets admin (no headers).
 */
function is_local_cs_target() {
    const base = (global.config && global.config.cs_analysis_path) || '';
    return /^https?:\/\/localhost(?::\d+)?\/?/i.test(base);
}

async function ensure_user_details() {
    if (cached_user_details) return cached_user_details;
    const server_base = (global.config && global.config.server_path) || '';
    if (!server_base) return null;
    const trimmed_server = server_base.replace(/\/+$/, '');
    try {
        const resp = await fetch(trimmed_server + '/user/get_details', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({}),
        });
        if (!resp.ok) return null;
        const j = await resp.json();
        cached_user_details = (j && j.user && j.user.user_details) || null;
        return cached_user_details;
    } catch (_) {
        return null;
    }
}

async function bootstrap_session() {
    if (bootstrap_done) return true;
    if (bootstrap_in_flight) return bootstrap_in_flight;

    bootstrap_in_flight = (async () => {
        const server_base = (global.config && global.config.server_path) || '';
        const cs_base = (global.config && global.config.cs_analysis_path) || '';
        if (!server_base || !cs_base) {
            throw new Error('cs_analysis_fetch bootstrap: missing server_path or cs_analysis_path');
        }

        const trimmed_server = server_base.replace(/\/+$/, '');
        const trimmed_cs = cs_base.replace(/\/+$/, '');

        // Step 1: introspect via infurnia_main. Cookie travels host-to-host.
        const introspect_resp = await fetch(trimmed_server + '/user/get_details', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({}),
        });
        if (!introspect_resp.ok) {
            throw new Error('cs_analysis_fetch bootstrap: /user/get_details ' + introspect_resp.status);
        }
        const introspect_json = await introspect_resp.json();
        const user_details = introspect_json && introspect_json.user && introspect_json.user.user_details;
        if (!user_details) {
            throw new Error('cs_analysis_fetch bootstrap: no user_details in /user/get_details response');
        }

        // Step 2: ask cs-analysis to mint a cross-subdomain signed cookie.
        const init_resp = await fetch(trimmed_cs + '/api/v1/auth/init', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Requested-With': 'cs-analysis-csp',
            },
            credentials: 'include',
            body: JSON.stringify({ user_details }),
        });
        if (!init_resp.ok) {
            const txt = await init_resp.text();
            throw new Error('cs_analysis_fetch bootstrap: /auth/init ' + init_resp.status + ' ' + txt.slice(0, 200));
        }

        bootstrap_done = true;
        return true;
    })();

    try {
        return await bootstrap_in_flight;
    } finally {
        bootstrap_in_flight = null;
    }
}

const cs_analysis_fetch = async ({ path, method = 'GET', body, query, headers }, _retried = false) => {
    if (!path) throw new Error('cs_analysis_fetch: path required');

    const base = (global.config && global.config.cs_analysis_path) || '';
    const trimmedBase = base.replace(/\/+$/, '');
    const trimmedPath = path.startsWith('/') ? path : '/' + path;
    let url = trimmedBase + trimmedPath;

    if (query && typeof query === 'object') {
        const params = new URLSearchParams();
        Object.entries(query).forEach(([k, v]) => {
            if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
        });
        const qs = params.toString();
        if (qs) url += '?' + qs;
    }

    const reqHeaders = {
        'Content-Type': 'application/json',
        'X-Requested-With': 'cs-analysis-csp',
        ...(headers || {}),
    };

    // Local cs-analysis: thread the real logged-in user through so the
    // backend's local bypass can build the correct session user (impersonation
    // by header). One extra introspection call on the very first request,
    // then cached for the session.
    if (is_local_cs_target()) {
        const u = await ensure_user_details();
        if (u && u.id && u.email) {
            reqHeaders['x-local-user-id'] = String(u.id);
            reqHeaders['x-local-user-email'] = String(u.email);
            const roles = (u.role && Array.isArray(u.role.store)) ? u.role.store : [];
            reqHeaders['x-local-user-roles'] = roles.join(',');
            if (u.first_name) reqHeaders['x-local-user-first-name'] = String(u.first_name);
            if (u.last_name) reqHeaders['x-local-user-last-name'] = String(u.last_name);
            if (u.store_id) reqHeaders['x-local-user-store-id'] = String(u.store_id);
        }
    }

    const options = {
        method,
        headers: reqHeaders,
        credentials: 'include',
    };
    if (body !== undefined && method !== 'GET' && method !== 'HEAD') {
        options.body = JSON.stringify(body);
    }

    let resp;
    try {
        resp = await fetch(url, options);
    } catch (err) {
        Sentry.captureException(err, { fingerprint: ['cs_analysis_fetch_network'] });
        throw new Error('cs-analysis network error');
    }

    if (resp.status === 401 && !_retried && path !== '/api/v1/auth/init') {
        try {
            await bootstrap_session();
            return cs_analysis_fetch({ path, method, body, query, headers }, true);
        } catch (boot_err) {
            console.warn('cs-analysis auth bootstrap failed', boot_err && boot_err.message);
            // fall through to surface the original 401
        }
    }

    let payload = null;
    const text = await resp.text();
    if (text) {
        try {
            payload = JSON.parse(text);
        } catch (_) {
            payload = text;
        }
    }

    if (!resp.ok) {
        const status = resp.status;
        if (status === 401 || status === 403) {
            console.warn('cs-analysis auth failed', status, payload);
        }
        const err = new Error(
            `cs-analysis ${status} ${resp.statusText} at ${path}: ` +
                (typeof payload === 'string' ? payload : JSON.stringify(payload)).slice(0, 300),
        );
        err.status = status;
        err.body = payload;
        throw err;
    }

    return payload;
};

window.cs_analysis_fetch = cs_analysis_fetch;
export default cs_analysis_fetch;
